import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Printer, ArrowLeft, Search, FileText, CheckCircle, Clock, XCircle } from 'lucide-react';
import { usePageTitle } from '../hooks/usePageTitle';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
export const INVOICE_STORAGE_KEY = 'nm_invoices';

// ── Helpers ─────────────────────────────────────────────────
function formatCurrency(amount) {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency', currency: 'IDR', minimumFractionDigits: 0,
  }).format(amount || 0);
}

function formatDate(dateStr, lang) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString(
    lang === 'id' ? 'id-ID' : 'en-US',
    { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }
  );
}

function formatDateShort(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('id-ID', {
    day: '2-digit', month: '2-digit', year: 'numeric'
  });
}

// ── localStorage helpers ─────────────────────────────────────
export function saveInvoiceToStorage(invoiceData) {
  try {
    const existing = JSON.parse(localStorage.getItem(INVOICE_STORAGE_KEY) || '[]');
    const filtered = existing.filter(i => i.booking_code !== invoiceData.booking_code);
    filtered.unshift({ ...invoiceData, saved_at: new Date().toISOString() });
    localStorage.setItem(INVOICE_STORAGE_KEY, JSON.stringify(filtered.slice(0, 20)));
  } catch { /* ignore */ }
}

export function getInvoicesFromStorage() {
  try {
    return JSON.parse(localStorage.getItem(INVOICE_STORAGE_KEY) || '[]');
  } catch { return []; }
}

// ── Status Badge ─────────────────────────────────────────────
function StatusBadge({ status, lang }) {
  const map = {
    pending:     { label: lang === 'id' ? 'Menunggu Pembayaran' : 'Pending Payment', color: 'bg-yellow-100 text-yellow-700', Icon: Clock },
    confirmed:   { label: lang === 'id' ? 'Dikonfirmasi' : 'Confirmed',              color: 'bg-green-100 text-green-700',   Icon: CheckCircle },
    checked_in:  { label: lang === 'id' ? 'Sedang Menginap' : 'Checked In',          color: 'bg-blue-100 text-blue-700',    Icon: CheckCircle },
    checked_out: { label: lang === 'id' ? 'Selesai' : 'Checked Out',                 color: 'bg-gray-100 text-gray-600',    Icon: CheckCircle },
    cancelled:   { label: lang === 'id' ? 'Dibatalkan' : 'Cancelled',                color: 'bg-red-100 text-red-600',      Icon: XCircle },
  };
  const s = map[status] || map.pending;
  const Icon = s.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-sans font-semibold ${s.color}`}>
      <Icon size={12} /> {s.label}
    </span>
  );
}

// ── Invoice Card (preview list) ──────────────────────────────
function InvoiceCard({ invoice, lang, onClick }) {
  return (
    <div
      onClick={() => onClick(invoice.booking_code)}
      className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm hover:shadow-md hover:border-primary-200 transition-all cursor-pointer"
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="font-mono font-bold text-primary-700 text-sm">{invoice.booking_code}</p>
          <p className="text-xs text-gray-400 font-sans mt-0.5">
            {lang === 'id' ? 'Disimpan' : 'Saved'}: {formatDateShort(invoice.saved_at)}
          </p>
        </div>
        <StatusBadge status={invoice.status} lang={lang} />
      </div>
      <div className="space-y-1 text-sm font-sans">
        <div className="flex justify-between">
          <span className="text-gray-500">{lang === 'id' ? 'Kamar' : 'Room'}</span>
          <span className="font-medium text-dark capitalize">{invoice.room_type || '-'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Check-in</span>
          <span className="text-dark">{formatDateShort(invoice.checkin_date)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Check-out</span>
          <span className="text-dark">{formatDateShort(invoice.checkout_date)}</span>
        </div>
        <div className="flex justify-between border-t border-gray-100 pt-2 mt-2">
          <span className="font-semibold text-dark">Total</span>
          <span className="font-bold text-primary-700">{formatCurrency(invoice.total_price)}</span>
        </div>
      </div>
    </div>
  );
}

// ── Invoice Detail ───────────────────────────────────────────
function InvoiceDetail({ invoice, lang, onBack }) {
  const code = invoice.booking_code || '-';
  const nights = invoice.num_nights || 0;
  const pricePerNight = invoice.price_per_night || 0;
  const subtotal = invoice.subtotal || (pricePerNight * nights);
  const discount = invoice.discount_amount || 0;
  const total = invoice.total_price || 0;

  const handlePrint = () => {
    const origin = window.location.origin;
    const html = `<!DOCTYPE html><html lang="${lang}"><head><meta charset="utf-8">
<title>Invoice ${code} - Ndalem Mataram</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap');
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:Inter,Arial,sans-serif;background:#fff;color:#1f2937;padding:40px;max-width:800px;margin:0 auto}
.header{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:20px;border-bottom:3px solid #562b18;margin-bottom:28px}
.brand-name{font-size:24px;font-weight:700;color:#562b18}
.brand-sub{font-size:12px;color:#6b7280;margin-top:4px}
.invoice-title{font-size:28px;font-weight:700;color:#1f2937;margin-bottom:4px}
.invoice-meta{font-size:12px;color:#6b7280}
.booking-code{display:inline-block;background:#d4881c;color:#fff;font-weight:700;font-size:20px;letter-spacing:2px;padding:10px 24px;border-radius:8px;margin:20px 0}
.section-title{font-size:13px;font-weight:700;color:#562b18;text-transform:uppercase;letter-spacing:.05em;margin-bottom:12px;padding-bottom:6px;border-bottom:1px solid #e5e7eb}
.grid-2{display:grid;grid-template-columns:1fr 1fr;gap:12px 32px;margin-bottom:24px}
.field-label{font-size:11px;color:#9ca3af;text-transform:uppercase;letter-spacing:.05em;margin-bottom:2px}
.field-value{font-size:14px;font-weight:600;color:#111827}
.line-items{width:100%;border-collapse:collapse;margin-bottom:20px}
.line-items th{background:#f9fafb;text-align:left;padding:10px 14px;font-size:12px;font-weight:700;color:#6b7280;border-bottom:2px solid #e5e7eb}
.line-items td{padding:10px 14px;font-size:13px;border-bottom:1px solid #f3f4f6}
.total-box{background:#fdf8f0;border:2px solid #d4881c;border-radius:12px;padding:16px 20px;display:flex;justify-content:space-between;align-items:center;margin-bottom:24px}
.total-label{font-size:14px;color:#6b7280}
.total-value{font-size:26px;font-weight:700;color:#562b18}
.policy-box{background:#f9fafb;border-radius:8px;padding:14px 18px;font-size:12px;color:#374151;margin-bottom:20px}
.policy-box p{margin-bottom:4px}
.footer{margin-top:32px;padding-top:20px;border-top:1px solid #e5e7eb;display:flex;justify-content:space-between;font-size:11px;color:#9ca3af}
@media print{body{padding:20px}}
</style></head><body>
<div class="header">
  <div>
    <div class="brand-name">Ndalem Mataram</div>
    <div class="brand-sub">Jl. K.H. Agus Salim No.18, Taman Baru, Banyuwangi 68416<br>+62 813-7857-5758 - ndalem.mataram1@gmail.com</div>
  </div>
  <div style="text-align:right">
    <div class="invoice-title">INVOICE</div>
    <div class="invoice-meta">${lang === 'id' ? 'Tanggal' : 'Date'}: ${formatDateShort(new Date().toISOString())}</div>
  </div>
</div>
<div class="booking-code">${code}</div>
<div class="section-title">${lang === 'id' ? 'Detail Tamu' : 'Guest Details'}</div>
<div class="grid-2">
  <div><div class="field-label">${lang === 'id' ? 'Nama' : 'Name'}</div><div class="field-value">${invoice.guest_name || '-'}</div></div>
  <div><div class="field-label">Email</div><div class="field-value">${invoice.guest_email || '-'}</div></div>
  <div><div class="field-label">${lang === 'id' ? 'Telepon' : 'Phone'}</div><div class="field-value">${invoice.guest_phone || '-'}</div></div>
</div>
<div class="section-title">${lang === 'id' ? 'Detail Pemesanan' : 'Booking Details'}</div>
<div class="grid-2">
  <div><div class="field-label">${lang === 'id' ? 'Tipe Kamar' : 'Room Type'}</div><div class="field-value">${invoice.room_type || '-'}</div></div>
  <div><div class="field-label">${lang === 'id' ? 'Jumlah Tamu' : 'Guests'}</div><div class="field-value">${invoice.num_guests || 1} ${lang === 'id' ? 'orang' : 'guests'}</div></div>
  <div><div class="field-label">Check-in</div><div class="field-value">${formatDate(invoice.checkin_date, lang)}</div></div>
  <div><div class="field-label">Check-out</div><div class="field-value">${formatDate(invoice.checkout_date, lang)}</div></div>
  <div><div class="field-label">${lang === 'id' ? 'Jumlah Malam' : 'Nights'}</div><div class="field-value">${nights} ${lang === 'id' ? 'malam' : 'nights'}</div></div>
</div>
<div class="section-title">${lang === 'id' ? 'Rincian Biaya' : 'Cost Breakdown'}</div>
<table class="line-items">
  <thead><tr><th>${lang === 'id' ? 'Deskripsi' : 'Description'}</th><th style="text-align:right">${lang === 'id' ? 'Jumlah' : 'Amount'}</th></tr></thead>
  <tbody>
    <tr><td>${invoice.room_type || 'Kamar'} x ${nights} ${lang === 'id' ? 'malam' : 'nights'} @ ${formatCurrency(pricePerNight)}</td><td style="text-align:right">${formatCurrency(subtotal)}</td></tr>
    ${discount > 0 ? `<tr><td style="color:#16a34a">${lang === 'id' ? 'Diskon' : 'Discount'} ${invoice.promo_code ? '(' + invoice.promo_code + ')' : ''}</td><td style="text-align:right;color:#16a34a">- ${formatCurrency(discount)}</td></tr>` : ''}
  </tbody>
</table>
<div class="total-box">
  <div class="total-label">${lang === 'id' ? 'Total Pembayaran' : 'Total Payment'}</div>
  <div class="total-value">${formatCurrency(total)}</div>
</div>
<div class="policy-box">
  <p><strong>${lang === 'id' ? 'Kebijakan' : 'Policies'}</strong></p>
  <p>${lang === 'id' ? '- Check-in: 14.00 WIB | Check-out: 12.00 WIB' : '- Check-in: 2:00 PM | Check-out: 12:00 PM'}</p>
  <p>${lang === 'id' ? '- Tunjukkan invoice ini kepada resepsionis saat check-in' : '- Present this invoice to reception at check-in'}</p>
  <p>${lang === 'id' ? '- Untuk pertanyaan: ndalem.mataram1@gmail.com' : '- Inquiries: ndalem.mataram1@gmail.com'}</p>
</div>
<div style="text-align:center;margin-bottom:20px">
  <p style="font-size:11px;color:#6b7280;margin-bottom:8px">${lang === 'id' ? 'Scan untuk verifikasi booking:' : 'Scan to verify booking:'}</p>
  <img src="https://chart.googleapis.com/chart?chs=120x120&cht=qr&chl=${encodeURIComponent(origin + '/verify/' + code)}&choe=UTF-8" width="120" height="120" style="border:1px solid #e5e7eb;border-radius:8px" />
  <p style="font-size:10px;color:#9ca3af;margin-top:4px;font-family:monospace">${origin}/verify/${code}</p>
</div>
<div class="footer">
  <span>Ndalem Mataram - ndalemmataram.com</span>
  <span>${lang === 'id' ? 'Terima kasih atas kepercayaan Anda' : 'Thank you for choosing us'}</span>
</div>
<script>window.onload=()=>window.print();</script>
</body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
  };

  return (
    <div className="max-w-3xl mx-auto">
      <button onClick={onBack} className="inline-flex items-center gap-2 text-gray-400 hover:text-gray-600 font-sans text-sm mb-6 transition-colors">
        <ArrowLeft size={15} />
        {lang === 'id' ? 'Kembali ke Daftar Invoice' : 'Back to Invoice List'}
      </button>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Header */}
        <div className="bg-primary-950 px-8 py-6 flex items-start justify-between">
          <div>
            <p className="text-accent-400 text-xs font-sans tracking-[0.2em] uppercase mb-1">Ndalem Mataram</p>
            <h2 className="font-display text-2xl text-white font-bold">Invoice</h2>
            <p className="text-white/50 text-xs font-sans mt-1">Jl. K.H. Agus Salim No.18, Banyuwangi</p>
          </div>
          <div className="text-right">
            <div className="bg-amber-600 text-white font-mono font-bold text-lg px-4 py-2 rounded-xl tracking-wider">
              {code}
            </div>
            <p className="text-white/50 text-xs font-sans mt-2">{formatDateShort(new Date().toISOString())}</p>
          </div>
        </div>

        <div className="p-8 space-y-6">
          {/* Status */}
          <div className="flex items-center justify-between">
            <StatusBadge status={invoice.status} lang={lang} />
            <span className="text-xs text-gray-400 font-sans">
              {lang === 'id' ? 'Dibuat' : 'Created'}: {formatDateShort(invoice.created_at || invoice.saved_at)}
            </span>
          </div>

          {/* Guest Info */}
          <div>
            <h3 className="text-xs font-sans font-bold text-gray-500 uppercase tracking-widest mb-3">
              {lang === 'id' ? 'Informasi Tamu' : 'Guest Information'}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-gray-50 rounded-xl p-4">
              {[
                { label: lang === 'id' ? 'Nama' : 'Name', value: invoice.guest_name },
                { label: 'Email', value: invoice.guest_email },
                { label: lang === 'id' ? 'Telepon' : 'Phone', value: invoice.guest_phone },
              ].map(({ label, value }) => (
                <div key={label}>
                  <p className="text-xs text-gray-400 font-sans">{label}</p>
                  <p className="text-sm font-semibold text-dark font-sans">{value || '-'}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Booking Details */}
          <div>
            <h3 className="text-xs font-sans font-bold text-gray-500 uppercase tracking-widest mb-3">
              {lang === 'id' ? 'Detail Pemesanan' : 'Booking Details'}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-gray-50 rounded-xl p-4">
              {[
                { label: lang === 'id' ? 'Tipe Kamar' : 'Room Type', value: invoice.room_type },
                { label: lang === 'id' ? 'Jumlah Tamu' : 'Guests', value: `${invoice.num_guests || 1} ${lang === 'id' ? 'orang' : 'guests'}` },
                { label: lang === 'id' ? 'Jumlah Malam' : 'Nights', value: `${nights} ${lang === 'id' ? 'malam' : 'nights'}` },
                { label: 'Check-in', value: formatDate(invoice.checkin_date, lang) },
                { label: 'Check-out', value: formatDate(invoice.checkout_date, lang) },
              ].map(({ label, value }) => (
                <div key={label}>
                  <p className="text-xs text-gray-400 font-sans">{label}</p>
                  <p className="text-sm font-semibold text-dark font-sans capitalize">{value || '-'}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Cost Breakdown */}
          <div>
            <h3 className="text-xs font-sans font-bold text-gray-500 uppercase tracking-widest mb-3">
              {lang === 'id' ? 'Rincian Biaya' : 'Cost Breakdown'}
            </h3>
            <div className="border border-gray-100 rounded-xl overflow-hidden">
              <div className="divide-y divide-gray-50">
                <div className="flex justify-between px-5 py-3 text-sm font-sans">
                  <span className="text-gray-500">
                    {invoice.room_type} x {nights} {lang === 'id' ? 'malam' : 'nights'} @ {formatCurrency(pricePerNight)}
                  </span>
                  <span className="font-medium text-dark">{formatCurrency(subtotal)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between px-5 py-3 text-sm font-sans text-green-600">
                    <span>{lang === 'id' ? 'Diskon' : 'Discount'} {invoice.promo_code && `(${invoice.promo_code})`}</span>
                    <span>- {formatCurrency(discount)}</span>
                  </div>
                )}
              </div>
              <div className="bg-amber-50 border-t-2 border-amber-300 flex justify-between px-5 py-4">
                <span className="font-bold text-dark font-sans">{lang === 'id' ? 'Total Pembayaran' : 'Total Payment'}</span>
                <span className="font-bold text-xl text-primary-700 font-sans">{formatCurrency(total)}</span>
              </div>
            </div>
          </div>

          {/* Policies */}
          <div className="bg-stone-50 rounded-xl p-4 text-xs font-sans text-gray-500 space-y-1">
            <p className="font-semibold text-gray-600 mb-2">{lang === 'id' ? 'Kebijakan' : 'Policies'}</p>
            <p>- Check-in: 14.00 WIB | Check-out: 12.00 WIB</p>
            <p>- {lang === 'id' ? 'Tunjukkan invoice ini kepada resepsionis saat check-in' : 'Present this invoice to reception at check-in'}</p>
            <p>- {lang === 'id' ? 'Pertanyaan:' : 'Inquiries:'} ndalem.mataram1@gmail.com</p>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3 pt-2">
            <button
              onClick={handlePrint}
              className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-primary-600 hover:bg-primary-500 text-white rounded-xl font-sans font-semibold text-sm transition-colors"
            >
              <Printer size={15} />
              {lang === 'id' ? 'Cetak / Download PDF' : 'Print / Download PDF'}
            </button>
            <Link
              to={`/verify/${code}`}
              className="flex items-center justify-center gap-2 px-5 py-3 border-2 border-primary-600 text-primary-600 hover:bg-primary-50 rounded-xl font-sans font-semibold text-sm transition-colors"
            >
              <CheckCircle size={15} />
              {lang === 'id' ? 'Verifikasi Booking' : 'Verify Booking'}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────
export default function InvoicePage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language;
  usePageTitle(lang === 'id' ? 'Invoice Saya' : 'My Invoices');

  const [invoices, setInvoices] = useState([]);
  const [selected, setSelected] = useState(null);
  const [searchCode, setSearchCode] = useState('');
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState('');

  useEffect(() => {
    setInvoices(getInvoicesFromStorage());
  }, []);

  useEffect(() => {
    if (code) {
      const found = getInvoicesFromStorage().find(i => i.booking_code === code.toUpperCase());
      if (found) {
        setSelected(found);
      } else {
        fetchFromAPI(code.toUpperCase());
      }
    }
  }, [code]);

  const fetchFromAPI = async (bookingCode) => {
    setFetching(true);
    setFetchError('');
    try {
      const res = await fetch(`${API}/bookings/verify/${bookingCode}`);
      const data = await res.json();
      if (!data.booking_code) throw new Error('not found');
      const invoice = {
        booking_code: data.booking_code,
        guest_name: data.guest_name,
        guest_email: '',
        guest_phone: '',
        room_type: data.room_type,
        checkin_date: data.checkin_date,
        checkout_date: data.checkout_date,
        num_nights: data.num_nights,
        num_guests: data.num_guests,
        price_per_night: data.num_nights ? data.total_price / data.num_nights : 0,
        subtotal: data.total_price,
        discount_amount: 0,
        total_price: data.total_price,
        status: data.booking_status,
        created_at: data.created_at,
        saved_at: new Date().toISOString(),
      };
      saveInvoiceToStorage(invoice);
      setInvoices(getInvoicesFromStorage());
      setSelected(invoice);
    } catch {
      setFetchError(
        lang === 'id'
          ? 'Kode booking tidak ditemukan. Pastikan kode sudah benar.'
          : 'Booking code not found. Please check the code.'
      );
    } finally {
      setFetching(false);
    }
  };

  const handleSearch = async () => {
    if (!searchCode.trim()) return;
    const upper = searchCode.trim().toUpperCase();
    const found = invoices.find(i => i.booking_code === upper);
    if (found) { setSelected(found); return; }
    await fetchFromAPI(upper);
  };

  if (selected) {
    return (
      <div className="min-h-screen bg-stone-50">
        <div className="bg-primary-950 pt-32 pb-12 px-6 text-center">
          <p className="text-accent-400 text-xs font-sans tracking-[0.3em] uppercase mb-2">Ndalem Mataram</p>
          <h1 className="font-display text-4xl text-white font-bold mb-2">Invoice</h1>
        </div>
        <div className="max-w-3xl mx-auto px-6 py-10">
          <InvoiceDetail
            invoice={selected}
            lang={lang}
            onBack={() => { setSelected(null); navigate('/invoice'); }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="bg-primary-950 pt-32 pb-12 px-6 text-center">
        <p className="text-accent-400 text-xs font-sans tracking-[0.3em] uppercase mb-2">Ndalem Mataram</p>
        <h1 className="font-display text-4xl text-white font-bold mb-2">
          {lang === 'id' ? 'Invoice Saya' : 'My Invoices'}
        </h1>
        <p className="text-white/50 font-sans text-sm">
          {lang === 'id' ? 'Riwayat pemesanan tersimpan di perangkat ini' : 'Booking history saved on this device'}
        </p>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10 space-y-6">
        {/* Search */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <h2 className="font-sans font-semibold text-dark mb-3">
            {lang === 'id' ? 'Cari Invoice via Kode Booking' : 'Find Invoice by Booking Code'}
          </h2>
          <div className="flex gap-3">
            <input
              type="text"
              value={searchCode}
              onChange={e => setSearchCode(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="NM-20260319-A3X"
              className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-mono focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
            />
            <button
              onClick={handleSearch}
              disabled={fetching}
              className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 hover:bg-primary-500 disabled:bg-gray-300 text-white rounded-xl font-sans font-semibold text-sm transition-colors"
            >
              {fetching
                ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                : <Search size={15} />}
              {lang === 'id' ? 'Cari' : 'Search'}
            </button>
          </div>
          {fetchError && <p className="text-red-500 text-sm font-sans mt-2">{fetchError}</p>}
        </div>

        {/* Daftar tersimpan */}
        {invoices.length > 0 ? (
          <div>
            <h2 className="font-sans font-semibold text-dark mb-3">
              {lang === 'id' ? 'Tersimpan di Perangkat Ini' : 'Saved on This Device'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {invoices.map(inv => (
                <InvoiceCard
                  key={inv.booking_code}
                  invoice={inv}
                  lang={lang}
                  onClick={(c) => setSelected(invoices.find(i => i.booking_code === c))}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-16 text-gray-400">
            <FileText size={48} className="mx-auto mb-4 opacity-30" />
            <p className="font-sans text-sm">
              {lang === 'id'
                ? 'Belum ada invoice tersimpan. Invoice otomatis tersimpan setelah booking.'
                : 'No invoices saved yet. Invoices are saved automatically after booking.'}
            </p>
          </div>
        )}

        <p className="text-center text-xs text-gray-400 font-sans">
          {lang === 'id'
            ? 'Invoice disimpan di browser ini. Gunakan kode booking untuk akses dari perangkat lain.'
            : 'Invoices are saved in this browser. Use your booking code to access from other devices.'}
        </p>
      </div>
    </div>
  );
}
